import clsx from 'clsx';
import { forwardRef, memo, type AllHTMLAttributes } from 'react';

import type { LayoutBaseProps } from './types';

import './Layout.css';
import { resolvePresetProps } from '../../utils/resolvePresetProps';
import { type LayoutPresetKey, presets } from './presets';

export type LayoutProps = LayoutBaseProps & {
  preset?: LayoutPresetKey;
  // `as` is owned by `LayoutBaseProps`. Without omitting it here, intersecting
  // with the native `as` string attribute collapses it to `ElementType & string`
  // and no component can be passed.
} & Omit<AllHTMLAttributes<HTMLElement>, 'wrap' | 'as'>;

export const Layout = memo(
  forwardRef<HTMLElement, LayoutProps>(function Layout(props, ref) {
    const { preset, ...propsWithoutPreset } = props;
    const presetFragment = preset ? presets[preset] : {};

    const {
      align,
      as: Component = 'div',
      children,
      className,
      direction = 'row',
      flex = 'none',
      justify,
      spacing = 'm',
      wrap,
      ...rest
    } = resolvePresetProps(
      propsWithoutPreset as LayoutProps,
      presetFragment as Partial<LayoutProps>,
    );

    return (
      <Component
        className={clsx(
          'eds-layout',
          `eds-layout--flex-${flex}`,
          `eds-layout--direction-${direction}--spacing-${spacing}`,
          {
            [`eds-layout--align-${align}`]: Boolean(align),
            [`eds-layout--justify-${justify}`]: Boolean(justify),
            'eds-layout--wrap': wrap === true,
          },
          className,
        )}
        {...rest}
        ref={ref}
      >
        {children}
      </Component>
    );
  }),
);

Layout.displayName = 'Layout';
